<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useNotification } from '@kyvg/vue3-notification'
import _ from 'lodash'
import SitesService from '@/services/SitesService'
import SiteAdminService from '@/services/SiteAdminService'
import { sitesPlanRefusal } from '@/Utils/sitesPlanLimits'
import { trackSites, SITES_EVENTS } from '@/lib/tracking/sites'
import { SITE_TEMPLATES, TEMPLATE_CATALOG, TEMPLATE_BROADCAST } from '@/components/Sites/siteTemplates'
import { AUDIENCE_PRESETS, AUDIENCE_DEFAULT_NEW } from '@/components/Sites/broadcastAudience'
import SiteTemplateThumb from '@/components/Sites/SiteTemplateThumb.vue'
import ModalBox from './ModalBox.vue'
import FormButton from '../Atoms/FormButton.vue'
import FormInput from '../Atoms/FormInput.vue'
import Alert from '../Atoms/Alert.vue'
import IconBase from '../icon/IconBase.vue'
import IconGlobe from '../icon/IconGlobe.vue'

const emit = defineEmits(['site-created', 'plan-refused'])

const store = useStore()
const { notify } = useNotification()

const isVisible = ref(false)
const name = ref('')
const slug = ref('')
// Home-page template (sites-broadcast-template-contract.md §7): always
// offered (no feature flag), catalog preselected.
const template = ref(TEMPLATE_CATALOG)
// Broadcast audience (site-admin API v0.19): label preset written to the
// runtime right after creation; castr's create body is unchanged.
const audience = ref(AUDIENCE_DEFAULT_NEW)
// Once the user edits the slug by hand we stop deriving it from the name.
const slugTouched = ref(false)
const slugCheck = ref(null) // last /sites/check-slug response
const checking = ref(false)
const creating = ref(false)
const error = ref(null)

// Client-side mirror of the server slugify — instant preview while the
// authoritative check-slug request is in flight.
const localSlugify = (input) => String(input || '')
	.normalize('NFKD')
	.replace(/[\u0300-\u036f]/g, '')
	.toLowerCase()
	.replace(/[^a-z0-9]+/g, '-')
	.replace(/-{2,}/g, '-')
	.replace(/^-+|-+$/g, '')
	.slice(0, 63)
	.replace(/-+$/g, '')

const primaryHost = computed(() => slugCheck.value?.primaryHost || null)
const slugValid = computed(() => Boolean(slugCheck.value?.valid && slugCheck.value?.available && slugCheck.value?.slug === slug.value))
const slugMessage = computed(() => {
	if (!slug.value || checking.value) return ''
	if (!slugCheck.value || slugCheck.value.slug !== slug.value) return ''
	if (slugCheck.value.valid && slugCheck.value.available) return ''
	return slugCheck.value.message || 'This address is not available'
})
const canSubmit = computed(() => name.value.trim().length > 0 && slug.value.length > 0 && slugValid.value && !checking.value && !creating.value)

const runCheck = _.debounce(async ({ mode, value }) => {
	if (!value) {
		slugCheck.value = null
		checking.value = false
		return
	}
	try {
		const res = mode === 'name'
			? await SitesService.suggestSlug(value)
			: await SitesService.checkSlug(value)
		// Ignore stale responses (user kept typing).
		if (mode === 'name' && (slugTouched.value || name.value.trim() !== value)) return
		if (mode === 'slug' && slug.value !== value) return
		if (mode === 'name' && res.slug) slug.value = res.slug
		slugCheck.value = res
	} catch (err) {
		slugCheck.value = { slug: value, valid: false, available: false, message: err.message || 'Could not check this address' }
	} finally {
		checking.value = false
	}
}, 350)

watch(name, (value) => {
	if (!isVisible.value || slugTouched.value) return
	const trimmed = value.trim()
	slug.value = localSlugify(trimmed)
	slugCheck.value = null
	if (!trimmed) return
	checking.value = true
	runCheck({ mode: 'name', value: trimmed })
})

const onSlugInput = (value) => {
	slugTouched.value = true
	const cleaned = String(value || '').toLowerCase().replace(/[^a-z0-9-]/g, '')
	slug.value = cleaned
	slugCheck.value = null
	if (!cleaned) return
	checking.value = true
	runCheck({ mode: 'slug', value: cleaned })
}

const resetSlugToName = () => {
	slugTouched.value = false
	const trimmed = name.value.trim()
	slug.value = localSlugify(trimmed)
	slugCheck.value = null
	if (!trimmed) return
	checking.value = true
	runCheck({ mode: 'name', value: trimmed })
}

const reset = () => {
	runCheck.cancel()
	name.value = ''
	slug.value = ''
	slugTouched.value = false
	slugCheck.value = null
	checking.value = false
	creating.value = false
	error.value = null
	template.value = TEMPLATE_CATALOG
	audience.value = AUDIENCE_DEFAULT_NEW
}

const show = () => {
	reset()
	isVisible.value = true
}

const close = () => {
	isVisible.value = false
}

watch(isVisible, (open) => {
	if (!open) reset()
})

const createSite = async () => {
	if (!canSubmit.value) return
	creating.value = true
	error.value = null
	try {
		const site = await store.dispatch('Sites/createSite', {
			name: name.value.trim(),
			slug: slug.value,
			template: template.value,
		})
		const caps = store.getters['Sites/caps']
		trackSites(SITES_EVENTS.SITE_CREATED, site, {
			template: site.template ? undefined : template.value,
			from: 'picker',
			slug: site.slug,
			sites_used: caps?.used,
			max_sites: caps?.maxSites,
		})
		if (template.value === TEMPLATE_BROADCAST) await saveAudience(site)
		notify({ group: 'success', text: `Site "${site.name}" created.` })
		emit('site-created', site)
		isVisible.value = false
	} catch (err) {
		// A plan refusal is not something to fix in this form: hand over to the upgrade popup.
		const kind = sitesPlanRefusal(err)
		if (kind) {
			isVisible.value = false
			emit('plan-refused', { kind, maxSites: err.payload?.maxSites ?? null, used: err.payload?.used ?? null })
			return
		}
		error.value = err.message || 'Could not create the site.'
		creating.value = false
	}
}

/** Best effort: the site exists either way, and Settings › Audience can set the preset later. */
const saveAudience = async (site) => {
	try {
		await SiteAdminService.updateSettings(site.id, { broadcastAudience: audience.value })
		trackSites(SITES_EVENTS.AUDIENCE_PRESET_SET, site, { preset: audience.value, surface: 'create', template: TEMPLATE_BROADCAST })
	} catch {
		notify({ group: 'info', text: 'The site was created, but its audience could not be saved. Set it under Settings › Audience.' })
	}
}

defineExpose({ show, close })
</script>

<template>
	<ModalBox v-model="isVisible" title="New Site" size="sm" scroll-body wrapper-classes="lg:h-auto lg:max-h-[85vh]">
		<form class="flex flex-col gap-4 px-4 pb-4 sm:px-6 sm:pb-6" @submit.prevent="createSite">
			<Alert v-if="error" type="error" class="!mb-0">
				{{ error }}
			</Alert>

			<fieldset class="m-0 min-w-0 border-0 p-0" data-testid="site-template-picker">
				<legend class="mb-2 text-tiny font-medium text-surface-text-1">Choose a template</legend>
				<div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
					<label v-for="t in SITE_TEMPLATES" :key="t.id"
						class="flex cursor-pointer flex-col gap-2 rounded-lg border p-2.5 transition-colors"
						:class="template === t.id ? 'border-main bg-surface-3' : 'border-surface-4 bg-surface-2 hover:border-surface-6'"
						:data-testid="`site-template-${t.id}`" :data-active="template === t.id">
						<input v-model="template" type="radio" name="site-template" :value="t.id" class="sr-only">
						<SiteTemplateThumb :template="t.id" :active="template === t.id" />
						<span class="flex flex-col gap-0.5">
							<span class="text-sm font-medium text-white">{{ t.title }}</span>
							<span class="text-tiny text-surface-7">{{ t.description }}</span>
						</span>
					</label>
				</div>
				<p class="mb-0 mt-1.5 text-tiny text-surface-7">You can switch templates later under Settings › General.</p>
			</fieldset>

			<fieldset v-if="template === TEMPLATE_BROADCAST" class="m-0 min-w-0 border-0 p-0" data-testid="site-audience-picker">
				<legend class="mb-2 text-tiny font-medium text-surface-text-1">What are you streaming?</legend>
				<div class="flex flex-col gap-2">
					<label v-for="p in AUDIENCE_PRESETS" :key="p.id"
						class="flex cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2.5 transition-colors"
						:class="audience === p.id ? 'border-main bg-surface-3' : 'border-surface-4 bg-surface-2 hover:border-surface-6'"
						:data-testid="`site-audience-${p.id}`" :data-active="audience === p.id">
						<input v-model="audience" type="radio" name="site-audience" :value="p.id" class="mt-1 size-3.5 shrink-0 accent-main">
						<span class="flex min-w-0 flex-col gap-0.5">
							<span class="text-sm font-medium text-white">{{ p.title }}</span>
							<span class="text-tiny text-surface-7">{{ p.description }}</span>
						</span>
					</label>
				</div>
				<p class="mb-0 mt-1.5 text-tiny text-surface-7">Only changes the wording viewers see. You can change it later under Settings › Audience.</p>
			</fieldset>

			<FormInput
				id="site-name"
				v-model="name"
				label="Site name"
				placeholder="Acme TV"
				:maxlength="80"
			/>

			<div>
				<FormInput
					id="site-slug"
					:model-value="slug"
					label="Site address"
					placeholder="acme-tv"
					:maxlength="63"
					:is-error="Boolean(slugMessage)"
					@update:model-value="onSlugInput"
				/>
				<div class="mt-1.5 flex items-start justify-between gap-2 text-tiny">
					<p v-if="slugMessage" class="mb-0 text-error">
						{{ slugMessage }}
					</p>
					<p v-else-if="checking" class="mb-0 text-surface-7">
						Checking availability…
					</p>
					<p v-else class="mb-0 text-surface-7">
						Lowercase letters, numbers and hyphens, 3–63 characters.
					</p>
					<button
						v-if="slugTouched && name"
						type="button"
						class="shrink-0 text-surface-8 underline hover:text-white"
						@click="resetSlugToName"
					>
						Use name
					</button>
				</div>
			</div>

			<div class="rounded-lg bg-surface-3 px-3 py-2.5">
				<p class="mb-1 text-tiny text-surface-7">Your site will be available at</p>
				<p class="mb-0 flex items-center gap-2 text-sm font-medium text-white break-all">
					<icon-base class="shrink-0 text-surface-8" :width="14" :height="14">
						<icon-globe />
					</icon-base>
					<span v-if="primaryHost && slugValid">https://{{ primaryHost }}</span>
					<span v-else class="text-surface-7">https://{{ slug || 'your-site' }}.…</span>
				</p>
				<p class="mb-0 mt-1.5 text-tiny text-surface-7">
					You can connect your own domain later. The address cannot be changed after the site is created.
				</p>
			</div>
		</form>

		<template #footer>
			<footer class="flex flex-col gap-2 border-t border-surface-3 p-4 sm:flex-row-reverse sm:p-6">
				<FormButton class="w-full justify-center sm:w-auto" :disabled="!canSubmit" :is-loading="creating" @click="createSite">
					Create Site
				</FormButton>
				<FormButton class="w-full justify-center sm:w-auto" type="secondary" @click="close">
					Cancel
				</FormButton>
			</footer>
		</template>
	</ModalBox>
</template>
